"use client"

import type React from "react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FiX, FiCreditCard, FiDollarSign, FiFileText, FiUser, FiCalendar, FiTrash2 } from "react-icons/fi"
import ConfirmationModal from "../../ui/confirmation-modal"

interface IPayment {
    id: number
    user_id: number
    amount: number
    currency: string
    payment_method: string
    status: string
    transaction_id: string
    description?: string
    created_at?: Date
    updated_at?: Date
    user?: {
        first_name: string
        last_name: string
        email: string
    }
}

interface ViewPaymentOffcanvasProps {
    showViewPaymentCanvas: boolean
    setShowViewPaymentCanvas: (show: boolean) => void
    payment: IPayment | null
}

const ViewPaymentOffcanvas: React.FC<ViewPaymentOffcanvasProps> = ({
    showViewPaymentCanvas,
    setShowViewPaymentCanvas,
    payment,
}) => {
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const handleClose = () => {
        setShowViewPaymentCanvas(false)
    }

    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (payment) {
            setLoading(true)
            try {
                // Simulate delete action
                await new Promise(resolve => setTimeout(resolve, 1000))
                toast.success(`Payment ${payment.transaction_id} deleted successfully!`)
                setDeleteConfirmOpen(false)
                setShowViewPaymentCanvas(false)
                // Here you would typically call an API to delete the payment
                // and refresh the payments list in the parent component
            } catch (err) {
                toast.error("Error deleting payment")
            } finally {
                setLoading(false)
            }
        }
    }

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case "completed":
                return "bg-green-100 text-green-800"
            case "pending":
                return "bg-yellow-100 text-yellow-800"
            case "failed":
                return "bg-red-100 text-red-800"
            case "refunded":
                return "bg-blue-100 text-blue-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    const formatDate = (date: Date | undefined) => {
        if (!date) return "N/A"
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    if (!payment) return null

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
                    showViewPaymentCanvas ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
                    showViewPaymentCanvas ? "translate-x-0" : "translate-x-full"
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiCreditCard className="mr-2" />
                        Payment Details
                    </h2>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Payment Overview */}
                        <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 rounded-lg border border-primary-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <FiDollarSign className="w-8 h-8 text-primary-600" />
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">
                                            {payment.currency} {payment.amount.toFixed(2)}
                                        </h3>
                                        <p className="text-sm text-gray-600">Transaction ID: {payment.transaction_id}</p>
                                    </div>
                                </div>
                                <span className={`px-3 py-2 capitalize text-sm font-medium rounded-full ${getStatusStyle(payment.status)}`}>
                                    {payment.status}
                                </span>
                            </div>
                        </div>

                        {/* User Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2 flex items-center">
                                <FiUser className="mr-2" />
                                User Information
                            </h3>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                                    <p className="text-gray-900 font-semibold">
                                        {payment.user?.first_name} {payment.user?.last_name}
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                    <p className="text-gray-900 font-semibold">{payment.user?.email}</p>
                                </div>

                            </div>
                        </div>

                        {/* Payment Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2 flex items-center">
                                <FiCreditCard className="mr-2" />
                                Payment Information
                            </h3>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                                    <p className="text-gray-900 font-semibold text-lg">
                                        {payment.currency} {payment.amount.toFixed(2)}
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                                    <p className="text-gray-900 font-semibold">{payment.currency}</p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                                    <p className="text-gray-900 font-semibold">{payment.payment_method}</p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                    <span className={`px-3 py-1 capitalize text-sm font-medium rounded-full ${getStatusStyle(payment.status)}`}>
                                        {payment.status}
                                    </span>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Transaction ID</label>
                                    <p className="text-gray-900 font-semibold font-mono text-sm bg-white px-3 py-2 rounded border">
                                        {payment.transaction_id}
                                    </p>
                                </div>
                            </div>

                            {payment.description && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <FiFileText className="mr-1" />
                                        Description
                                    </label>
                                    <p className="text-gray-900">{payment.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Timestamp Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2 flex items-center">
                                <FiCalendar className="mr-2" />
                                Timestamp Information
                            </h3>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Created At</label>
                                    <p className="text-gray-900 font-semibold">{formatDate(payment.created_at)}</p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Updated At</label>
                                    <p className="text-gray-900 font-semibold">{formatDate(payment.updated_at)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 p-6 mb-20">
                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={handleDeleteClick}
                                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
                            >
                                <FiTrash2 className="w-4 h-4 mr-2" />
                                Delete Payment
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmOpen && (
                <ConfirmationModal
                    isOpen={deleteConfirmOpen}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onConfirm={handleDeleteConfirm}
                    title="Delete Payment"
                    description={`Are you sure you want to delete payment ${payment.transaction_id}? This action cannot be undone.`}
                    loading={loading}
                />
            )}
        </>
    )
}

export default ViewPaymentOffcanvas